<template>
    <cod-dialog
        ref="dialog"
        :show.sync="showDialog"
        max-width="500px"
    >
        <!-- Header -->
        <div slot="header">
            <div>{{ $t('order_edit.order_item') }}</div>
        </div>

        <!-- Content -->
        <div slot="content" class="item-form">
            <v-row>
                <v-col class="pt-3 pb-0" cols="12">
                    <v-text-field
                        v-model="form.name"
                        :error-messages="form.$errors.name"
                        :label="$t('general.name')"
                        @blur="form.validate('name')"
                        @input="delete form.$errors.name"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" sm="6">
                    <number-field
                        v-model="form.unit_price"
                        :error-messages="form.$errors.unit_price"
                        :label="$t('general.unit_price')"
                        :precision="3"
                        @blur="form.validate('unit_price')"
                        @input="delete form.$errors.unit_price"
                    />
                </v-col>
                <v-col cols="12" sm="6">
                    <number-field
                        v-model="form.discount_percent"
                        :label="$t('general.discount')"
                        :precision="2"
                        suffix="%"
                        :error-messages="form.$errors.discount_percent"
                        @blur="form.validate('discount_percent')"
                        @keyup.enter="focusField('marginPercent')"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" sm="6">
                    <number-field
                        v-model="form.count"
                        :error-messages="form.$errors.count"
                        :label="$t('general.quantity')"
                        @blur="form.validate('count')"
                        @input="delete form.$errors.count"
                    />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-combobox
                        v-model="form.unit_of_measure"
                        :error-messages="form.$errors.unit_of_measure"
                        :label="$t('general.unit_of_measure')"
                        :items="unitsOfMeasure"
                        item-value="key"
                        item-text="value"
                        @blur="form.validate('unit_of_measure')"
                        @input="delete form.$errors.unit_of_measure"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-select
                        v-model="form.tax_rate_id"
                        :error-messages="form.$errors.tax_rate_id"
                        :label="$t('general.tax_rate')"
                        :items="availableTaxRates"
                        item-text="display"
                        item-value="id"
                        @change="form.validate('tax_rate_id')"
                    />
                </v-col>
            </v-row>
        </div>

        <!-- Footer -->
        <template slot="footer">
            <v-btn
                color="primary"
                :disabled="form.$busy"
                :loading="form.$busy"
                class="flex-1"
                @click="save"
            >
                {{ $t('general.save') }}
            </v-btn>
        </template>
    </cod-dialog>
</template>

<script>
import NumberField from '@/components/NumberField';
import CodDialog from '@/components/CodDialog';
import ApiService from '@/services/ApiService';
import Product from '@/models/Product';
import TaxRate from '@/models/TaxRate';
import OrderItem from '@/models/OrderItem';
import { required, minValue } from 'vuelidate/lib/validators';

export default {
    components: {
        NumberField,
        CodDialog,
    },

    props: {
        show: {
            type: Boolean,
            default: false,
        },
        item: {
            type: Object,
            default: () => null,
        },
        order: {
            type: Object,
            default: () => null,
        },
        products: {
            type: Array,
            default: () => Product.all(),
        },
        taxRates: {
            type: Array,
            default: () => TaxRate.all()
        },
    },

    data() {
        return {
            showDialog: false,
            form: null,
            picker1: null,
            picker2: null,
        };
    },

    validations() {
        return {
            form: {
                name: { required },
                unit_price: { required, minValue: minValue(0.001) },
                unit_of_measure: { required },
                discount_percent: { required, minValue: minValue(0), maxValue: minValue(99) },
                tax_rate_id: { required },
                count: { required, minValue: minValue(0.001) },
            },
        };
    },

    created() {
        this.form = this.createForm(this.item);
    },

    watch: {
        show(val) {
            this.showDialog = val;

            if (val) {
                this.form = this.createForm(this.item);
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);
        },
    },

    computed: {
        unitsOfMeasure() {
            const predefinedUnits = this.appConfig.product.unitsOfMeasure
            const selectedUnits = this.products.map(p => p.unit_of_measure)
            const allUnits = [...predefinedUnits, ...selectedUnits].filter((unit, index, array) => index === array.indexOf(unit))

            const unitsOfMeasure = allUnits.map(unit => {
                return {
                    key: unit,
                    value: this.$t(`products.unit_of_measure.${unit}`) !== `products.unit_of_measure.${unit}`
                        ? this.$t(`products.unit_of_measure.${unit}`)
                        : unit
                }
            })

            return unitsOfMeasure;
        },

        availableTaxRates() {
            let taxRates = TaxRate.productTaxRates()

            taxRates.forEach(taxRate => {
                taxRate.display = taxRate.name
                    ? `${taxRate.name} (${taxRate.label}) - ${taxRate.rate}%`
                    : `${taxRate.label} - ${taxRate.rate}%`
            });

            return taxRates;
        },
    },

    methods: {
        createForm(item) {
            return new Form(this, 'form', {
                id: item?.id || null,
                name: item?.name || null,
                order_id: item?.order_id || this.order?.id,
                order_hash: item?.order_hash || this.order?.hash,
                unit_of_measure: item?.getUnitOfMeasure() || this.unitsOfMeasure[0].value,
                unit_price: item ? parseFloat(item.unit_price) : null,
                count: item ? parseFloat(item.count) : 1,
                discount_percent: item ? parseFloat(item.discount_percent) : 0,
                tax_rate_id: item?.tax_rate_id || this.availableTaxRates[0]?.id || null,
                tax_rate: item?.tax_rate || null,
                product_id: item?.product_id || null,
            });
        },

        prepareForSave() {
            this.form.tax_rate = TaxRate.find(this.form.tax_rate_id).rate;
            this.form.unit_of_measure = typeof this.form.unit_of_measure === 'object' && this.form.unit_of_measure !== null
                ? this.form.unit_of_measure.key
                : this.form.unit_of_measure
        },

        async save() {
            if (!this.form.validate()) return;

            this.prepareForSave();

            const method = this.form.id ? 'patch' : 'post';
            const url = `/admin/orders/${this.order.id}/items${(this.form.id ? `/${this.form.id}` : '')}`;

            const response = await ApiService[method](url, this.form);
            this.$emit('save', new OrderItem(response.data));
            this.$snackbar(this.$t('order_edit.order_item_saved'), 'success');

            this.form = this.createForm();
            this.showDialog = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.item-form {
    padding: 24px;
    min-height: 280px;
}
</style>
